import type { ExperimentDefinition } from "@/api/types";

/**
 * Computes a stable bucket [0-99] from sessionId and experimentId using SHA-256.
 * The same inputs always produce the same bucket, ensuring deterministic assignment.
 */
export async function getBucket(
  sessionId: string,
  experimentId: string,
): Promise<number> {
  // Convert the sessionId:experimentId string to bytes
  const input = `${sessionId}:${experimentId}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(input);

  // Hash the bytes and store them in an array
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = new Uint8Array(hashBuffer);

  // Use first 4 bytes to create a 32-bit unsigned integer
  const value =
    (hashArray[0] << 24) |
    (hashArray[1] << 16) |
    (hashArray[2] << 8) |
    hashArray[3];

  // Convert to unsigned and mod 100 for bucket [0-99]
  return (value >>> 0) % 100;
}

/**
 * Assigns a variant for a single experiment based on the session's bucket.
 * Iterates through variants in definition order, returning the first where
 * bucket < cumulative weight.
 */
export async function assignVariant(
  sessionId: string,
  experiment: ExperimentDefinition,
): Promise<string> {
  const bucket = await getBucket(sessionId, experiment.experimentId);
  const variants = Object.entries(experiment.variants);

  // Iterate through the experiment variants until the bucket matches the experiment weight
  let cumulativeWeight = 0;
  for (const [variantName, variantDefinition] of variants) {
    cumulativeWeight += variantDefinition.weight;
    if (bucket < cumulativeWeight) {
      return variantName;
    }
  }

  // Fallback: return last variant (handles bucket === 99 with weights summing to 100)
  return variants[variants.length - 1][0];
}

/**
 * Assigns variants for all experiments, returning a map of experimentId -> variantName.
 * Returns empty object if experiments array is empty or undefined.
 */
export async function assignAllExperiments(
  sessionId: string,
  experiments: ExperimentDefinition[] | undefined,
): Promise<Record<string, string>> {
  // If no experiments return empty object
  if (!experiments || experiments.length === 0) {
    return {};
  }

  // Iterate through each experiment and assign a group for each
  const assignments: Record<string, string> = {};
  for (const experiment of experiments) {
    assignments[experiment.experimentId] = await assignVariant(
      sessionId,
      experiment,
    );
  }

  return assignments;
}
